import { extendTheme, type ThemeConfig } from '@chakra-ui/react';

// Color mode configuration
const config: ThemeConfig = {
    initialColorMode: 'dark',
    useSystemColorMode: false,
};

/**
 * Custom color palette
 * These should be kept in sync with CSS variables in global.css
 */
const colors = {
    brand: {
        50: '#e3f2fd',
        100: '#bbdefb',
        200: '#90caf9',
        300: '#64b5f6',
        400: '#42a5f5',
        500: '#2196f3',
        600: '#1e88e5',
        700: '#1976d2',
        800: '#1565c0',
        900: '#0d47a1',
    },
    surface: {
        100: '#2d2d30',
        200: '#252526',
        300: '#1e1e1e',
        400: '#181818',
        500: '#121212',
    },
    // Mode-specific accent colors (sync with --color-bim-*, --color-2d-*, --color-3d-*)
    bim: {
        primary: '#0078d4',
        secondary: '#005a9e',
        accent: '#00b4ff',
    },
    '2d': {
        primary: '#ff6600',
        secondary: '#cc5200',
        accent: '#ff8533',
    },
    '3d': {
        primary: '#00cc66',
        secondary: '#009944',
        accent: '#33ff99',
    },
    sketch: {
        primary: '#e04938',
        secondary: '#b03628',
        accent: '#ff7a63',
    },
    // Status colors (sync with --color-success, --color-warning, --color-error, --color-info)
    status: {
        success: '#4caf50',
        warning: '#ff9800',
        error: '#f44336',
        info: '#00aaff',
    },
    // Selection colors (sync with --color-selection-*)
    selection: {
        window: '#0066ff',
        crossing: '#00cc00',
        highlight: '#00aaff',
        hover: '#ffaa00',
    },
    // Snap colors (sync with --color-snap-*)
    snap: {
        endpoint: '#00ff00',
        midpoint: '#00ffff',
        center: '#ff00ff',
        intersection: '#ffff00',
        perpendicular: '#00aaff',
        tangent: '#ff8800',
        quadrant: '#ff0088',
        nearest: '#88ff00',
        node: '#ffffff',
        extension: '#66d9ff',
        parallel: '#ffcc44',
        grid: '#888888',
    },
    // Grid colors (sync with --color-grid-*, --color-axis-*)
    grid: {
        major: '#4a4a4a',
        minor: '#3a3a3a',
        section: '#6a6a6a',
    },
    axis: {
        x: '#ff4444',
        y: '#44ff44',
        z: '#4444ff',
    },
};

// Component style overrides
const components = {
    Button: {
        baseStyle: {
            fontWeight: 'medium',
            borderRadius: 'sm',
        },
        variants: {
            cad: {
                bg: 'transparent',
                border: '1px solid',
                borderColor: 'transparent',
                _hover: {
                    bg: 'whiteAlpha.100',
                    borderColor: 'whiteAlpha.300',
                },
                _active: {
                    bg: 'brand.500',
                    color: 'white',
                },
            },
            toolbar: {
                bg: 'transparent',
                p: 2,
                minW: 'auto',
                _hover: {
                    bg: 'whiteAlpha.100',
                },
                _active: {
                    bg: 'brand.500',
                },
            },
        },
    },
    Input: {
        variants: {
            cad: {
                field: {
                    bg: 'surface.300',
                    border: '1px solid',
                    borderColor: 'whiteAlpha.200',
                    fontFamily: 'mono',
                    fontSize: 'sm',
                    _focus: {
                        borderColor: 'brand.500',
                        boxShadow: 'none',
                    },
                },
            },
        },
    },
    Tooltip: {
        baseStyle: {
            bg: 'bg.panel',
            color: 'text.primary',
            fontSize: 'xs',
            px: 2,
            py: 1,
            borderRadius: 'sm',
            border: '1px solid',
            borderColor: 'border.base',
            boxShadow: 'md',
            zIndex: 'tooltip',
            '--tooltip-bg': 'colors.bg.panel', // Expose as CSS variable if needed explicitly, though Chakra does this internally
        },
    },
    Alert: {
        // Toast notifications render through the Alert component (variant
        // "solid"). Every status shares one neutral, theme-driven panel with
        // the brand accent only — Apple HIG-style, no per-status colors.
        variants: {
            solid: {
                container: {
                    bg: 'bg.panel',
                    color: 'text.primary',
                    borderWidth: '1px',
                    borderColor: 'border.base',
                    fontSize: 'sm',
                },
                icon: {
                    color: 'brand.500',
                },
                spinner: {
                    color: 'brand.500',
                },
                title: {
                    fontWeight: 'semibold',
                },
                description: {
                    color: 'text.secondary',
                },
            },
        },
    },
    Menu: {
        baseStyle: {
            list: {
                bg: 'surface.200',
                border: '1px solid',
                borderColor: 'whiteAlpha.200',
                shadow: 'lg',
            },
            item: {
                bg: 'transparent',
                _hover: {
                    bg: 'whiteAlpha.100',
                },
                _focus: {
                    bg: 'whiteAlpha.100',
                },
            },
        },
    },
};

// Typography
const fonts = {
    heading: "'Inter', 'Segoe UI', 'Roboto', sans-serif",
    body: "'Inter', 'Segoe UI', 'Roboto', sans-serif",
    mono: "'JetBrains Mono', 'Consolas', monospace",
};

// Global styles
const styles = {
    global: {
        body: {
            bg: 'surface.300',
            color: 'gray.100',
        },
        '*::placeholder': {
            color: 'gray.500',
        },
    },
};

export const theme = extendTheme({
    config,
    colors,
    components,
    fonts,
    styles,
    semanticTokens: {
        colors: {
            'bg.canvas': {
                default: 'gray.50',
                _dark: 'surface.300',
            },
            // Sketch workbench canvas (SketchUp-style light ground): deliberately
            // mode-independent — the sketch edge inks are always dark, so the
            // canvas stays light in dark mode too (mirrors
            // --color-bg-viewport-sketch).
            'bg.canvas.sketch': {
                default: '#bfbfc6',
                _dark: '#bfbfc6',
            },
            // CAD 2D drafting canvas: follows the color mode — the classic
            // near-black (not pure black) model space in dark, the light
            // drafting canvas in light (mirrors --color-bg-viewport).
            'bg.cad': {
                default: '#d0d0d0',
                _dark: 'surface.500',
            },
            // 3D engine viewport surface (quad cells + perspective): the
            // classic CAD gray in light, its dark counterpart in dark mode
            // (mirrors the --color-bg-viewport token sheet values).
            'bg.viewport': {
                default: '#d0d0d0',
                _dark: '#2a2a2a',
            },
            'bg.panel': {
                default: 'white',
                _dark: 'surface.200',
            },
            'bg.toolbar': {
                default: '#f0f0f0',
                _dark: 'surface.100',
            },
            'bg.hover': {
                default: 'blackAlpha.50',
                _dark: 'whiteAlpha.100',
            },
            'bg.active': {
                default: 'brand.50',
                _dark: 'whiteAlpha.200',
            },
            'border.base': {
                default: 'gray.200',
                _dark: 'whiteAlpha.200',
            },
            'border.hover': {
                default: 'gray.300',
                _dark: 'whiteAlpha.400',
            },
            'text.primary': {
                default: 'gray.900',
                _dark: 'gray.100',
            },
            'text.secondary': {
                default: 'gray.600',
                _dark: 'gray.400',
            },
            'icon.base': {
                default: 'gray.600',
                _dark: 'gray.400',
            },
        },
    },
});
